import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { Beaker, Play } from 'lucide-react';
import { backtestAPI, marketplaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const STRATEGIES = [
  { id: 'sma_cross', label: 'SMA Cross', defaults: { fast: 20, slow: 50 } },
  { id: 'rsi_bounds', label: 'RSI Bounds', defaults: { period: 14, oversold: 30, overbought: 70 } },
  { id: 'buy_hold', label: 'Buy & Hold', defaults: {} },
];

// Hoisted chart prop objects — prevents Recharts re-mounting axes/tooltip on
// every parent re-render.
const BT_TICK = { fontSize: 11 };
const BT_DOMAIN = ['dataMin', 'dataMax'];
const btFormatter = (v) => `$${Number(v).toLocaleString()}`;

const Backtest = () => {
  const { isPro } = useAuth();
  const { toast } = useToast();
  const [symbol, setSymbol] = useState('AAPL');
  const [strategy, setStrategy] = useState('sma_cross');
  const [period, setPeriod] = useState('2y');
  const [params, setParams] = useState({ fast: 20, slow: 50 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pubName, setPubName] = useState('');

  const updateStrategy = (s) => {
    setStrategy(s);
    const def = STRATEGIES.find((x) => x.id === s)?.defaults || {};
    setParams(def);
  };

  const run = async () => {
    setLoading(true);
    try {
      const r = await backtestAPI.run({ symbol: symbol.toUpperCase(), strategy, params, period });
      setResult(r);
    } catch (e) {
      toast({ title: 'Backtest failed', description: e?.response?.data?.detail || 'Try a different symbol', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const publish = async () => {
    if (!pubName.trim() || !result) return;
    try {
      // Downsample equity curve to <=40 points so the marketplace document
      // stays small and the sparkline still reads as a meaningful trajectory.
      const curve = result.equity_curve || [];
      const stride = Math.max(1, Math.ceil(curve.length / 40));
      const equity_preview = curve
        .filter((_, idx) => idx % stride === 0 || idx === curve.length - 1)
        .map((d) => ({ date: d.date, value: d.equity }));

      await marketplaceAPI.publish({
        name: pubName,
        description: `${symbol.toUpperCase()} · ${strategy} · period ${period}`,
        strategy,
        params,
        backtest_summary: { ...result.stats, equity_preview },
      });
      toast({ title: 'Published', description: 'Available in Marketplace.' });
      setPubName('');
    } catch (e) {
      toast({ title: 'Publish failed', description: e?.response?.data?.detail || 'Pro subscription required', variant: 'destructive' });
    }
  };

  const data = result?.equity_curve?.map((d) => ({ date: d.date, equity: d.equity })) || [];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Beaker className="text-purple-600" size={28} /> Strategy Backtester
          </h1>
          <p className="text-gray-500">Test SMA cross, RSI bounds, or buy-and-hold over historical data.</p>
        </div>

        <Card className="mb-6" data-testid="backtest-form">
          <CardContent className="p-4 grid md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-gray-600">Symbol</label>
              <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} data-testid="bt-symbol" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Strategy</label>
              <select className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm h-10" value={strategy} onChange={(e) => updateStrategy(e.target.value)} data-testid="bt-strategy">
                {STRATEGIES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Period</label>
              <select className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm h-10" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="bt-period">
                {['6mo', '1y', '2y', '5y', '10y'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="text-xs text-gray-600">Params</label>
              <div className="flex gap-1">
                {Object.entries(params).map(([k, v]) => (
                  <Input key={k} type="number" value={v} title={k} onChange={(e) => setParams({ ...params, [k]: Number(e.target.value) })} data-testid={`bt-param-${k}`} />
                ))}
              </div>
            </div>
            <div className="flex items-end">
              <Button onClick={run} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="bt-run-btn">
                <Play size={14} className="mr-1" /> {loading ? 'Running…' : 'Run'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {result && (
          <>
            <div className="grid md:grid-cols-5 gap-3 mb-6">
              {[
                ['Total Return', `${result.stats.total_return_pct}%`, result.stats.total_return_pct >= 0],
                ['CAGR', `${result.stats.cagr_pct}%`, result.stats.cagr_pct >= 0],
                ['Sharpe', `${result.stats.sharpe}`, result.stats.sharpe > 0],
                ['Max DD', `${result.stats.max_dd_pct}%`, false],
                ['Win Rate', `${result.stats.win_rate_pct}% (${result.stats.trades})`, result.stats.win_rate_pct >= 50],
              ].map(([k, v, good]) => (
                <Card key={k}><CardContent className="p-3"><p className="text-xs text-gray-500">{k}</p><p className={`text-xl font-bold ${good ? 'text-green-600' : 'text-red-600'}`} data-testid={`bt-stat-${k.replace(/\s+/g, '-').toLowerCase()}`}>{v}</p></CardContent></Card>
              ))}
            </div>

            <Card className="mb-6" data-testid="bt-equity-chart">
              <CardHeader><CardTitle className="text-lg">Equity Curve</CardTitle></CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" minTickGap={40} tick={BT_TICK} />
                      <YAxis tick={BT_TICK} domain={BT_DOMAIN} />
                      <Tooltip formatter={btFormatter} />
                      <Legend />
                      <Line type="monotone" dataKey="equity" stroke="#8b5cf6" dot={false} strokeWidth={2} name="Strategy" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-500 mt-2">Buy &amp; Hold reference: {result.buy_hold_stats?.total_return_pct}% over the same period.</p>
              </CardContent>
            </Card>

            {isPro && (
              <Card className="mb-6" data-testid="bt-publish-card">
                <CardHeader><CardTitle className="text-lg">Publish to Marketplace</CardTitle></CardHeader>
                <CardContent className="flex gap-2">
                  <Input placeholder="Strategy name e.g. AAPL SMA 20/50" value={pubName} onChange={(e) => setPubName(e.target.value)} data-testid="bt-pub-name" />
                  <Button className="bg-purple-600 hover:bg-purple-700" onClick={publish} disabled={!pubName.trim()} data-testid="bt-publish-btn">Publish</Button>
                </CardContent>
              </Card>
            )}

            <Card data-testid="bt-trades-card">
              <CardHeader><CardTitle className="text-lg">Trade Log</CardTitle></CardHeader>
              <CardContent>
                {!result.trades.length ? <p className="text-gray-500 text-sm">No trades.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-gray-600 border-b">
                        <tr><th>Entry</th><th>Exit</th><th className="text-right">Return</th></tr>
                      </thead>
                      <tbody>
                        {result.trades.map((t) => (
                          <tr key={`${t.entry_date}-${t.exit_date}`} className="border-b">
                            <td className="py-1.5">{t.entry_date} <span className="text-gray-400">${t.entry_price}</span></td>
                            <td>{t.exit_date} <span className="text-gray-400">${t.exit_price}</span> {t.open && <Badge variant="outline" className="ml-1 text-xs">open</Badge>}</td>
                            <td className={`text-right font-medium ${t.return_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{t.return_pct >= 0 ? '+' : ''}{t.return_pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Backtest;
